import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import './Profile.css';

const Profile = ({ userRole }) => {
  const { t } = useLanguage();
  const userId = localStorage.getItem('userId');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    password: '',
    confirmPassword: '',
    clientType: 'private',
    companyName: '',
    vatNumber: '',
    experience: 0,
    hourlyPrice: '',
    currency: 'USD'
  });
  const [userPhoto, setUserPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(true);

  useEffect(() => {
    loadUserData();
  }, [userId, userRole]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/profile');
      const userData = response.data.data;

      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        address: userData.address || '',
        city: userData.city || '',
        zipCode: userData.zipCode || '',
        password: '',
        confirmPassword: '',
        clientType: userData.clientType || 'private',
        companyName: userData.companyName || '',
        vatNumber: userData.vatNumber || '',
        experience: userData.experience || 0,
        hourlyPrice: (userData.hourlyPrice !== null && userData.hourlyPrice !== undefined) ? userData.hourlyPrice.toString() : '',
        currency: userData.currency || 'USD'
      });
      
      // Load user photo if exists
      if (userData.photo) {
        setUserPhoto(userData.photo);
        setPhotoPreview(userData.photo);
      }

      // Update localStorage with latest user data
      localStorage.setItem('userData', JSON.stringify(userData));
    } catch (error) {
      console.error('Error loading user data:', error);
      setMessage({ type: 'error', text: t('profile.errorLoading', 'Error loading profile data') });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: t('profile.invalidImageType', 'Please select a valid image file.') });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: t('profile.imageTooLarge', 'Image size should be less than 5MB.') });
        return;
      }

      // Read file as base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setUserPhoto(base64String);
        setPhotoPreview(base64String);
        setMessage({ type: '', text: '' });
      };
      reader.onerror = () => {
        setMessage({ type: 'error', text: t('profile.errorReadingImage', 'Error reading image file.') });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setUserPhoto(null);
    setPhotoPreview(null);
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validate password if provided
    if (formData.password) {
      if (formData.password.length < 6) {
        setMessage({ type: 'error', text: t('profile.passwordTooShort', 'Password must be at least 6 characters long') });
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setMessage({ type: 'error', text: t('profile.passwordMismatch', 'Passwords do not match') });
        return;
      }
    }

    // Validate company fields for company clients
    if (userRole === 'client' && formData.clientType === 'company') {
      if (!formData.companyName || !formData.vatNumber) {
        setMessage({ type: 'error', text: t('profile.companyRequired', 'Company name and VAT number are required for company clients') });
        return;
      }
    }

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city || undefined,
        zipCode: formData.zipCode || undefined,
        ...(userPhoto !== null && { photo: userPhoto || null }),
        ...(formData.password && { password: formData.password }),
        ...(userRole === 'client' && {
          clientType: formData.clientType,
          ...(formData.clientType === 'company' && {
            companyName: formData.companyName,
            vatNumber: formData.vatNumber
          })
        }),
        ...(userRole === 'cleaner' && {
          experience: parseInt(formData.experience) || 0
        })
      };

      const response = await api.put('/users/profile', updateData);
      const updatedUser = response.data.data;

      // Update localStorage with latest user data
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      
      // Dispatch custom event to update sidebar
      window.dispatchEvent(new Event('profileUpdated'));

      // Clear password fields
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));

      setMessage({ type: 'success', text: t('profile.profileUpdated', 'Profile updated successfully!') });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || t('profile.errorUpdating', 'Error updating profile. Please try again.');
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>{t('profile.myProfile', 'My Profile')}</h2>
        <p className="profile-subtitle">{t('profile.manageAccount', 'Manage your account information')}</p>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-section">
          <h3>{t('profile.personalInformation', 'Personal Information')}</h3>
          
          <div className="form-group photo-upload-group">
            <label>{t('profile.profilePhoto', 'Profile Photo')}</label>
            <div className="photo-upload-container">
              <div className="photo-preview">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile" className="profile-photo-preview" />
                ) : (
                  <div className="photo-placeholder">
                    <span className="photo-icon">📷</span>
                    <span className="photo-text">{t('profile.noPhoto', 'No Photo')}</span>
                  </div>
                )}
              </div>
              {isEditing && (
                <div className="photo-upload-actions">
                  <label htmlFor="photo-upload" className="btn btn-secondary btn-upload">
                    {photoPreview ? t('profile.changePhoto', 'Change Photo') : t('profile.uploadPhoto', 'Upload Photo')}
                  </label>
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  {photoPreview && (
                    <button
                      type="button"
                      className="btn btn-danger btn-remove"
                      onClick={handleRemovePhoto}
                    >
                      {t('profile.removePhoto', 'Remove Photo')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label>{t('auth.fullName', 'Full Name')} *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={!isEditing}
              className={!isEditing ? 'input-disabled' : ''}
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label>{t('auth.email', 'Email')} *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={!isEditing}
              className={!isEditing ? 'input-disabled' : ''}
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label>{t('auth.phone', 'Phone')} *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              disabled={!isEditing}
              className={!isEditing ? 'input-disabled' : ''}
              placeholder="Enter your phone number"
            />
          </div>

          <div className="form-group">
            <label>{t('auth.address', 'Address')} *</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              disabled={!isEditing}
              className={!isEditing ? 'input-disabled' : ''}
              placeholder="Enter your address"
            />
          </div>

          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>{t('auth.city', 'City')}</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                disabled={!isEditing}
                className={!isEditing ? 'input-disabled' : ''}
                placeholder={t('auth.cityPlaceholder', 'City')}
              />
            </div>
            <div className="form-group">
              <label>{t('auth.zipCode', 'Zip Code')}</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                disabled={!isEditing}
                className={!isEditing ? 'input-disabled' : ''}
                placeholder={t('auth.zipCodePlaceholder', 'Zip code')}
              />
            </div>
          </div>
        </div>

        {userRole === 'client' && (
          <div className="form-section">
            <h3>{t('profile.clientInformation', 'Client Information')}</h3>
            
            <div className="form-group">
              <label>{t('auth.clientType', 'Client Type')} *</label>
              <select
                name="clientType"
                value={formData.clientType}
                onChange={handleChange}
                required
                disabled={!isEditing}
                className={!isEditing ? 'input-disabled' : ''}
              >
              <option value="private">{t('auth.private', 'Private')}</option>
              <option value="company">{t('auth.company', 'Company')}</option>
              </select>
            </div>

            {formData.clientType === 'company' && (
              <>
                <div className="form-group">
                  <label>{t('auth.companyName', 'Company Name')} *</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required={formData.clientType === 'company'}
                    disabled={!isEditing}
                    className={!isEditing ? 'input-disabled' : ''}
                    placeholder="Enter company name"
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.vatNumber', 'VAT Number')} *</label>
                  <input
                    type="text"
                    name="vatNumber"
                    value={formData.vatNumber}
                    onChange={handleChange}
                    required={formData.clientType === 'company'}
                    disabled={!isEditing}
                    className={!isEditing ? 'input-disabled' : ''}
                    placeholder="Enter VAT number"
                  />
                </div>
              </>
            )}

            {/* Read-only hourly price for client */}
            <div className="form-group">
              <label>{t('profile.hourlyPrice', 'Hourly Price')}</label>
              <input
                type="text"
                value={
                  formData.hourlyPrice
                    ? `${formData.hourlyPrice} ${formData.currency || ''}`.trim()
                    : t('profile.hourlyPriceNotSet', 'Set by admin (not available)')
                }
                disabled
                readOnly
              />
              <p className="form-hint">
                {t(
                  'profile.hourlyPriceReadOnlyHint',
                  'Your hourly price is set by the admin and cannot be changed here.'
                )}
              </p>
            </div>
          </div>
        )}

        {userRole === 'cleaner' && (
          <div className="form-section">
            <h3>{t('profile.professionalInformation', 'Professional Information')}</h3>
            
            <div className="form-group">
              <label>Experience (years)</label>
              <input
                type="number"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                min="0"
                disabled={!isEditing}
                className={!isEditing ? 'input-disabled' : ''}
                placeholder="Years of experience"
              />
            </div>

            {/* Read-only hourly price for cleaner */}
            <div className="form-group">
              <label>{t('profile.hourlyPrice', 'Hourly Price')}</label>
              <input
                type="text"
                value={
                  formData.hourlyPrice
                    ? `${formData.hourlyPrice} ${formData.currency || ''}`.trim()
                    : t('profile.hourlyPriceNotSet', 'Set by admin (not available)')
                }
                disabled
                readOnly
              />
              <p className="form-hint">
                {t(
                  'profile.hourlyPriceReadOnlyHint',
                  'Your hourly price is set by the admin and cannot be changed here.'
                )}
              </p>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="form-section">
            <h3>Change Password</h3>
            <p className="form-hint">Leave blank if you don't want to change your password</p>
            
            <div className="form-group">
              <label>{t('profile.newPassword', 'New Password')}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                minLength="6"
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div className="form-group">
              <label>{t('profile.confirmNewPassword', 'Confirm New Password')}</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                minLength="6"
                placeholder="Confirm new password"
              />
            </div>
          </div>
        )}

        {message.text && (
          <div className={`message ${message.type === 'success' ? 'success-message' : 'error-message'}`}>
            {message.text}
          </div>
        )}

        {isEditing ? (
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={loadUserData}
            >
              Cancel
            </button>
          </div>
        ) : (
          <p className="saved-hint">
            {t('profile.savedHint', 'Profile saved. Log out and log in again to make further changes.')}
          </p>
        )}
      </form>
    </div>
  );
};

export default Profile;

